import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Boxes, Barcode, Users, Utensils, Wine, PawPrint, ShoppingBag, ArrowRight, MessageSquare, FileText, ShoppingCart, PackageCheck, MapPin, Handshake, Star, FileDown } from "lucide-react";
import { api, API } from "@/lib/api";
import { SectionTag, IconStat } from "@/components/Section";
import { ProductCard } from "@/components/ProductCard";

const catIcons = { Food: Utensils, Beverage: Wine, Pet: PawPrint, Specialty: ShoppingBag };
const catBlurb = {
  Food: "Snacks • Sauces • Mole • Desserts • Cooking Oils",
  Beverage: "Carbonated Beverages • Michelada Mixers • Specialty Drinks",
  Pet: "Dog Beer • Pet Beverages • Bone Broth",
  Specialty: "Novelty • Gift • Lifestyle • Functional",
};
const steps = [
  { n: 1, icon: MessageSquare, t: "Inquiry & Product Selection", d: "Contact ShelfReady AI to discuss category needs, target channels and volume requirements. Identify products of interest from the catalog." },
  { n: 2, icon: FileText, t: "Quotation & Availability", d: "We confirm current inventory, pricing, case-pack details and availability at the time of inquiry. All figures are subject to confirmation." },
  { n: 3, icon: ShoppingCart, t: "Order Placement", d: "Submit the confirmed order and required quantities. Multi-product orders across categories are welcome." },
  { n: 4, icon: PackageCheck, t: "Fulfillment & Shipment", d: "Orders are picked, verified, packed, labeled and staged for carrier pickup from our U.S. distribution operation." },
];

export default function Home() {
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data));
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/products").then((r) => {
      const seen = new Set();
      setFeatured(r.data.filter((p) => !seen.has(p.brand_slug) && seen.add(p.brand_slug)).slice(0, 8));
    });
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-16 lg:pt-24 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 rise">
            <SectionTag>Consumer Brands · Stronger Communities · Brighter Tomorrows</SectionTag>
            <h1 className="mt-6 font-extrabold uppercase tracking-tight leading-[0.92] text-[#0B1F4B] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">
              Curated products <span className="block font-medium text-[#1E7CF2] tracking-normal">for modern retail</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">Distinctive food, beverage, pet and specialty brands for today's retail consumer.</p>
            <p className="mt-3 text-sm text-slate-500 max-w-xl leading-relaxed">ShelfReady AI connects differentiated consumer brands with retail opportunities through reliable U.S. distribution, scalable supply and buyer-ready support.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/catalog" className="sr-btn-primary" data-testid="hero-browse-catalog">Browse the catalog <ArrowRight size={16} /></Link>
              <Link to="/contact" className="sr-btn-outline" data-testid="hero-request-quote">Request wholesale quote</Link>
            </div>
          </div>
          <div className="lg:col-span-5 rise rise-2">
            <div className="sr-card-tint p-6 grid grid-cols-2 gap-6">
              {["Food", "Beverage", "Pet", "Specialty"].map((c) => {
                const Icon = catIcons[c];
                return (
                  <Link key={c} to={`/catalog?category=${c}`} data-testid={`hero-category-${c.toLowerCase()}`} className="flex items-center gap-3 group">
                    <div className="sr-ring-icon bg-white group-hover:bg-[#1E7CF2] group-hover:text-white transition-colors"><Icon size={20} /></div>
                    <div><p className="text-xs font-bold uppercase tracking-wider">{c}</p><p className="text-[11px] text-slate-500">{cats.find((x) => x.name === c)?.count ?? "—"} products</p></div>
                  </Link>
                );
              })}
            </div>
            <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-[#1E7CF2] font-semibold text-right">Great products reach further</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 mt-12 rise rise-3">
          <img src="/brands/hero-lineup.jpg" alt="ShelfReady AI brand lineup" className="w-full rounded-t-3xl object-cover max-h-[480px] shadow-2xl shadow-[#0B1F4B]/10" />
        </div>
        <div className="border-t border-slate-200 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            <IconStat icon={Truck} title="U.S.-Based Distribution" testId="stat-distribution" />
            <IconStat icon={Boxes} title="Case & Pallet Supply" testId="stat-case-pallet" />
            <IconStat icon={Barcode} title="Retail-Ready Products" testId="stat-retail-ready" />
            <IconStat icon={Users} title="Samples Available Upon Request" testId="stat-samples" />
          </div>
        </div>
      </section>

      {/* BRAND COLLECTION */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionTag>Brand collection</SectionTag>
            <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold uppercase text-[#0B1F4B]">One supplier. <span className="text-[#1E7CF2]">More possibilities.</span></h2>
            <p className="mt-3 max-w-2xl text-sm sm:text-base text-slate-600">A growing portfolio across multiple consumer categories through one distribution relationship. Consolidating sourcing reduces vendor complexity and opens multi-category wholesale orders.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={`${API}/catalog/sell-sheet.pdf`} target="_blank" rel="noreferrer" className="sr-btn-primary" data-testid="home-catalog-deck-download"><FileDown size={16} /> Catalog PDF</a>
            <Link to="/brands" className="sr-btn-outline" data-testid="home-all-brands">All brands <ArrowRight size={16} /></Link>
          </div>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {brands.map((b, i) => (
            <Link to={`/brands/${b.slug}`} key={b.slug} data-testid={`brand-card-${b.slug}`} className={`sr-card sr-lift overflow-hidden group ${i === 0 ? "sm:col-span-2 lg:col-span-2" : ""}`}>
              <div className={`overflow-hidden ${i === 0 ? "aspect-[21/9]" : "aspect-[16/9]"}`}>
                <img src={b.image} alt={b.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5 flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: b.accent }}>{b.category} · {b.product_count} SKUs</p>
                  <h3 className="text-xl font-bold text-[#0B1F4B] mt-1">{b.name}</h3>
                  <p className="text-sm text-slate-500">{b.tagline}</p>
                </div>
                <ArrowRight className="text-[#1E7CF2] shrink-0 mt-2 transition-transform group-hover:translate-x-1" size={18} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <SectionTag>Portfolio overview</SectionTag>
            <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold uppercase text-[#0B1F4B]">Multiple categories. One reliable source.</h2>
            <p className="mt-4 text-sm text-slate-600">From premium avocado oil to bold Mexican sauces, the portfolio spans multiple retail channels and consumer occasions.</p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
            {["Food", "Beverage", "Pet", "Specialty"].map((c) => {
              const Icon = catIcons[c];
              return (
                <Link key={c} to={`/catalog?category=${c}`} data-testid={`category-tile-${c.toLowerCase()}`} className="sr-card-tint sr-lift p-6 flex gap-4">
                  <div className="sr-ring-icon bg-white shrink-0"><Icon size={20} /></div>
                  <div><h3 className="font-bold uppercase text-[#0B1F4B]">{c}</h3><p className="text-xs text-[#1E40AF] mt-1 font-medium">{catBlurb[c]}</p></div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20">
        <SectionTag>Featured products</SectionTag>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <h2 className="text-3xl sm:text-4xl font-extrabold uppercase text-[#0B1F4B]">Ready for the shelf</h2>
          <Link to="/catalog" className="text-sm font-semibold text-[#1E7CF2] hover:underline" data-testid="home-view-catalog">View full catalog →</Link>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{featured.map((p) => <ProductCard key={p.product_id} p={p} />)}</div>
      </section>

      {/* HOW TO ORDER */}
      <section className="bg-[#0B1F4B] text-white grain">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20 relative">
          <SectionTag light>Ordering process</SectionTag>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold uppercase">How to order <span className="text-[#5AA2FF]">from ShelfReady AI.</span></h2>
          <p className="mt-3 max-w-2xl text-sm text-slate-300">Whether you are a first-time buyer or an established wholesale account, the process follows the same organized steps.</p>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {steps.map((s) => (
              <div key={s.n} className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md p-6 flex gap-5" data-testid={`order-step-${s.n}`}>
                <div className="relative shrink-0"><div className="w-14 h-14 rounded-full border-2 border-[#5AA2FF] flex items-center justify-center text-[#5AA2FF]"><s.icon size={22} /></div><span className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-[#1E7CF2] text-[11px] font-bold flex items-center justify-center">{s.n}</span></div>
                <div><h3 className="font-bold uppercase">{s.t}</h3><p className="mt-1 text-sm text-slate-300">{s.d}</p></div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex justify-between items-center flex-wrap gap-4">
            <p className="text-xs uppercase tracking-[0.25em] text-[#9DC1F5]">From inquiry to shipment. Clear. Organized. Reliable.</p>
            <Link to="/how-to-order" className="sr-btn-blue" data-testid="home-how-to-order">Wholesale details</Link>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20">
        <SectionTag>Retail reach. Real relationships.</SectionTag>
        <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold uppercase text-[#0B1F4B]">Serving 1,000+ pet stores across the U.S.</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {[[MapPin, "1,000+ Pet Stores", "Retail reach across the United States."], [Handshake, "National Retail Relationships", "Including Five Below."], [Star, "Featured at Alibaba.com CoCreate 2026 LA", "Connecting brands, buyers and opportunities."]].map(([Icon, t, d], i) => (
            <div key={t} className="sr-card-tint p-6 flex gap-4 sr-lift" data-testid={`trust-card-${i}`}>
              <div className="sr-ring-icon bg-white shrink-0"><Icon size={20} /></div>
              <div><h3 className="font-bold uppercase text-[#0B1F4B] leading-tight">{t}</h3><p className="text-sm text-slate-600 mt-1">{d}</p></div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
